/**
 * InlineFieldGroup — the shell a multi-field entity opens inside.
 *
 * A GROUP EXPANDS WHERE IT SITS. The four registration fields, the driver's personal details, the
 * witness's two printed ones: at rest each is one summary row in the canvas, and opening it pushes
 * the rows below it down rather than covering them with a sheet. Nothing moves, nothing is hidden
 * behind the thing being edited, and closing it puts the page back exactly as it was.
 *
 * ONE COMMIT BAR PER GROUP, AT THE GROUP'S FOOT — never one per field. The bar is drawn from
 * `group.dirty` and nothing else, it says how many fields it is about to send, and it is the only
 * thing on this surface that writes.
 *
 * A DIRTY GROUP CANNOT BE COLLAPSED. Folding an edited group away would take the commit bar, the
 * verbatim refusal and the only visible evidence of unsaved work with it, leaving a draft alive
 * behind a closed summary row — which is the exact failure the reducer exists to prevent, arriving
 * through the chrome instead of through a refetch. The head keeps working; it just cannot hide
 * work in progress, and it says `· Unsaved` while it holds any.
 *
 * `Esc` REVERTS THE GROUP the caret is in — standard input semantics, bound per group so it means
 * something, and announced nowhere.
 *
 * A REFUSED FIELD TAKES THE CARET (§A4: "focus moves there"). The group is the only thing that can
 * do it: `useInlineGroup` knows the field NAMES and this file knows the DOM, and the ids belong to
 * neither — they are written at the call site (`f-insValue`, `f-svc12Start`). So the move is made
 * against the markup instead: after a commit comes back refused, the first control inside this
 * group that has gone `aria-invalid` is focused. Every primitive on the canvas sets that flag from
 * the same `error` prop, so the rule needs no map and cannot fall out of date with one.
 */

import { ChevronRight } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';

import CommitBar from './CommitBar';
import { blockKeyHandler } from './blockKeys';
import type { InlineGroup } from './useInlineGroup';

interface Props {
    /** The `#b-…` id a manifest row jumps to, when the group is the whole block. */
    id?: string;
    group: InlineGroup;
    /**
     * The collapsed head. Omit for a group that is always open — the parties block, where the
     * fields are the block.
     */
    summary?: {
        /** Left column, same rhythm as a record row: `Registration`. */
        label: string;
        /** Right column at rest: what the group currently holds, in one line. */
        value: ReactNode;
        open: boolean;
        onToggle: () => void;
    };
    /** Overrides the derived `3 fields commit together` where the group's write is unusual. */
    hint?: string;
    /** The quiet line under the fields: what they print on, what an empty one means. */
    footnote?: ReactNode;
    children: ReactNode;
}

export default function InlineFieldGroup({
    id,
    group,
    summary,
    hint,
    footnote,
    children,
}: Props) {
    // Dirty wins over collapsed, always. See the file header.
    const open = summary ? summary.open || group.dirty : true;
    const bodyId = id ? `${id}-fields` : undefined;

    const bodyRef = useRef<HTMLDivElement>(null);
    /**
     * ON THE TRANSITION OUT OF `saving`, AND ONLY THERE. Watching `fieldErrors` instead would fire
     * on every keystroke — the reducer rebuilds that object on each edit — and drag the caret to
     * whichever field is still refused while somebody is typing in another one.
     */
    const wasSaving = useRef(false);
    useEffect(() => {
        if (group.saving) {
            wasSaving.current = true;
            return;
        }
        if (!wasSaving.current) return;
        wasSaving.current = false;
        if (group.state.status !== 'error') return;
        bodyRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    }, [group.saving, group.state.status]);

    return (
        <div className="sd-grp" id={id}>
            {summary && (
                <button
                    type="button"
                    className="sd-grp-head"
                    aria-expanded={open}
                    aria-controls={bodyId}
                    onClick={summary.onToggle}
                >
                    <span className="sd-rec-k">{summary.label}</span>
                    <span className="sd-rec-v">
                        {summary.value}
                        {group.dirty && <span className="sd-grp-flag"> · Unsaved</span>}
                        {group.rejected && <span className="sd-grp-bad"> · {group.rejected}</span>}
                    </span>
                    <ChevronRight className="sd-grp-chev h-3.5 w-3.5" />
                </button>
            )}

            {/* The body carries no class of its own: the fields bring their own geometry, and an
                unstyled hook class is a rule Tailwind purges and a caller then cannot find. */}
            {open && (
                <div id={bodyId} ref={bodyRef} onKeyDown={blockKeyHandler(group.discard)}>
                    {children}

                    {/* The block's own sentence, verbatim, under the fields it was raised about.
                        Field-scoped refusals sit under their own inputs; this is what is left. */}
                    {group.error && <p className="sd-f-err sd-grp-err">{group.error}</p>}

                    {footnote && <p className="sd-hint">{footnote}</p>}

                    {group.dirty && (
                        <CommitBar
                            saving={group.saving}
                            hint={hint ?? group.hint}
                            onSave={group.commit}
                            onDiscard={group.discard}
                        />
                    )}

                    {/* The provenance line PERSISTS — a saved block that goes silent again is a
                        block an admin has to remember having saved. */}
                    {group.savedAt && !group.dirty && (
                        <p className="sd-prov">Saved {group.savedAt}</p>
                    )}
                </div>
            )}
        </div>
    );
}
